import { useEffect, useMemo, useRef } from "react";
import { XMarkIcon } from "../assets/icons/index.js";
import BorderBox from "./BorderBox.jsx";
import mediaManager from "../utils/mediaManager.js";

const ALLOWED_IFRAME_ATTRS = [
  "src",
  "title",
  "allow",
  "allowfullscreen",
  "referrerpolicy",
  "width",
  "height",
];

function parseIframeAttrs(html) {
  if (typeof html !== "string" || !html.trim()) return null;

  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const iframe = doc.querySelector("iframe");
    if (!iframe) return null;

    const src = iframe.getAttribute("src");
    if (!src) return null;

    const parsed = new URL(src, window.location.origin);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
      return null;
    }

    const attrs = {};
    for (const name of ALLOWED_IFRAME_ATTRS) {
      const value = iframe.getAttribute(name);
      if (value != null) attrs[name] = value;
    }
    return attrs;
  } catch {
    return null;
  }
}

export default function GrandMasterMessageModal({
  videoIframe,
  scaleIn,
  onClose,
  modalRef,
}) {
  const iframeAttrs = useMemo(
    () => parseIframeAttrs(videoIframe),
    [videoIframe],
  );

  const iframeRef = useRef(null);
  const playerId = useRef(
    `grandmaster-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
  );

  useEffect(() => {
    if (!iframeAttrs || !iframeRef.current) return;

    const currentPlayerId = playerId.current;

    mediaManager.players.set(currentPlayerId, {
      element: iframeRef.current,
      type: "video",
      isPlaying: false,
    });

    mediaManager.requestPlay(currentPlayerId, true);

    return () => {
      mediaManager.unregisterPlayer(currentPlayerId);
    };
  }, [iframeAttrs]);

  return (
    <div
      className={`fixed z-50 top-16 left-0 lg:left-24 right-0 bottom-0 px-6 pb-4 transition-all duration-300 ease-in-out outline-none ${
        scaleIn ? "opacity-100 scale-100" : "opacity-0 scale-95"
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="grand-master-modal-title"
      ref={modalRef}
      tabIndex={-1}
    >
      <BorderBox noPadding className="relative !h-full">
        <div
          id="grand-master-modal-title"
          className="absolute top-3 left-1/2 transform -translate-x-1/2 text-lz-ink-mid dark:text-white text-lg font-semibold z-10 text-center uppercase whitespace-nowrap"
        >
          Grand Master&apos;s Message
        </div>

        <button
          type="button"
          className="absolute top-6 right-6 p-3 rounded-lg bg-black/10 hover:bg-black/20 dark:bg-white/20 dark:hover:bg-white/40 text-lz-ink-mid dark:text-white transition-all duration-200 z-50 shadow-lg"
          onClick={onClose}
          aria-label="Close Grand Master's Message"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>

        <div className="w-full h-full flex items-center justify-center p-8 pt-20">
          {iframeAttrs ? (
            <div className="w-full max-w-5xl aspect-video">
              <iframe
                ref={iframeRef}
                {...iframeAttrs}
                title={iframeAttrs.title || "Grand Master's video message"}
                className="w-full h-full border-0"
              />
            </div>
          ) : (
            <div className="text-lz-ink-mid dark:text-white italic">
              No video available.
            </div>
          )}
        </div>
      </BorderBox>
    </div>
  );
}
